// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import { createWidgetElement } from "@insite/client-framework/Components/ContentItemStore";
import { makeHandlerChainAwaitable } from "@insite/client-framework/HandlerCreator";
import validateReCaptcha from "@insite/client-framework/Store/Components/ReCaptcha/Handlers/ValidateReCaptcha";
import addAccount from "@insite/client-framework/Store/Data/Accounts/Handlers/AddAccount";
import { getPageLinkByPageType } from "@insite/client-framework/Store/Links/LinksSelectors";
import translate from "@insite/client-framework/Translate";
import { LinkFieldValue } from "@insite/client-framework/Types/FieldDefinition";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import ToasterContext from "@insite/mobius/Toast/ToasterContext";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import CreateAccountBasicInfoForm from "@rpsc/App/Components/CreateAccount/CreateAccountBasicInfoForm";
import CreateAccountBillingAndShippingInfo from "@rpsc/App/Components/CreateAccount/CreateAccountBillingAndShippingInfo";
import Notify from "@rpsc/App/Components/Notify";
import { xLoadingOverlay } from "@rpsc/App/Handlers/XNG/Common";
import { CustomAppState } from "@rpsc/App/Store/Reducers";
import { StepperComponent } from "@rpsc/App/Widgets/Stepper/Stepper";
import { StepperContext } from "@rpsc/App/Widgets/Stepper/StepperContext";
import StyledBox from "@rpsc/XDL/Components/Common/Box";
import { delay } from "@rpsc/XDL/Utilities";
import React, { RefObject, useContext, useRef, useState } from "react";
import ReCAPTCHA from "react-google-recaptcha";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    submitButtonLabel = "submitButtonLabel",
    termsText = "termsText",
    termsLinkText = "termsLinkText",
    termsLink = "termsLink",
}
interface OwnProps extends WidgetProps {
    fields: {
        [fields.submitButtonLabel]: string;
        [fields.termsText]: string;
        [fields.termsLinkText]: string;
        [fields.termsLink]: LinkFieldValue;
    };
}

const mapStateToProps = (state: CustomAppState) => ({
    signInPageUrl: getPageLinkByPageType(state, "SignInPage")?.url,
});

const mapDispatchToProps = {
    addAccount,
    validateReCaptcha: makeHandlerChainAwaitable<{ location: string; recaptchaRef: RefObject<ReCAPTCHA> }, boolean>(
        validateReCaptcha,
    ),
    xLoadingOverlay,
};

type Props = OwnProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface CreateAccountNewNetStyles {
    wrapper?: InjectableCss;
    stepper?: InjectableCss;
    errorMsgWrapper?: InjectableCss;
    errroMsgNotify?: InjectableCss;
}

export const createAccountNewNetStyles: CreateAccountNewNetStyles = {};

const styles = createAccountNewNetStyles;

const CreateAccountNewNet = ({ signInPageUrl, fields, addAccount, validateReCaptcha, xLoadingOverlay }: Props) => {
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [errorMessage, setErrorMessage] = useState("");
    const toasterContext = useContext(ToasterContext);
    const { currentStep, showStep, setCurrentStep } = useContext(StepperContext);
    const recaptchaRef: RefObject<ReCAPTCHA> = React.createRef();

    const { submitButtonLabel, termsText, termsLinkText, termsLink } = fields;

    const goNextStep = () => {
        setErrorMessage("");
        setCurrentStep(currentStep + 1);
    };

    const goToPrevStep = () => {
        setCurrentStep(currentStep - 1);
    };

    const handleSubmitAddressesInfo = async () => {
        const isReCaptchaValid = await validateReCaptcha({ location: "CreateAccount", recaptchaRef });
        if (!isReCaptchaValid) {
            return;
        }
        xLoadingOverlay({ key: "add", value: "createAccount_NetNew" });

        setIsSubmitting(true);

        addAccount({
            isGuestPlusAccount: true,
            onSuccess: async () => {
                setErrorMessage("");
                toasterContext.addToast({ body: translate("Account created successfully!"), messageType: "success" });
                await delay();
                if (signInPageUrl) {
                    window.location.href = signInPageUrl;
                }
            },
            onError: (error: any) => {
                setIsSubmitting(false);
                setErrorMessage(error);
            },
            onComplete(resultProps: any) {
                xLoadingOverlay({ key: "clear", value: "createAccount_NetNew" });
                if (resultProps.apiResult?.successful) {
                    this.onSuccess?.();
                } else if (resultProps.apiResult?.errorMessage) {
                    this.onError?.(resultProps.apiResult.errorMessage);
                }
            },
        } as any);
    };

    const termsAndConditions = createWidgetElement("CreateAccount/TermsAndConditionsMessage", {
        fields: { ...{ termsLink, termsText, termsLinkText } },
    });

    return (
        <StyledBox data-test-selector="create-account-new-net" {...styles.wrapper}>
            {errorMessage && (
                <StyledWrapper {...styles.errorMsgWrapper}>
                    <Notify type="danger" {...styles.errroMsgNotify}>
                        {errorMessage}
                    </Notify>
                </StyledWrapper>
            )}
            <StyledWrapper data-test-selector="form-stepper" {...styles.stepper}>
                <StepperComponent />
            </StyledWrapper>
            {showStep(1) && <CreateAccountBasicInfoForm onSubmit={goNextStep} setErrorMessage={setErrorMessage} />}
            {showStep(2) && (
                <CreateAccountBillingAndShippingInfo
                    onSubmit={handleSubmitAddressesInfo}
                    onBackClick={goToPrevStep}
                    termsAndConditions={termsAndConditions}
                    isFormSubmitting={isSubmitting}
                    submitBtnLabel={submitButtonLabel}
                />
            )}
        </StyledBox>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(CreateAccountNewNet),
    definition: {
        group: "Create Account",
        fieldDefinitions: [
            {
                fieldType: "Translatable",
                name: fields.submitButtonLabel,
                displayName: "Submit Button Label",
                editorTemplate: "TextField",
                defaultValue: "Create",
            },
            {
                fieldType: "General",
                name: fields.termsText,
                displayName: "Terms And Conditions Text",
                editorTemplate: "RichTextField",
                extendedConfig: { height: 100 },
                defaultValue: "",
            },
            {
                fieldType: "General",
                name: fields.termsLinkText,
                displayName: "Terms and Conditions Link Label",
                editorTemplate: "TextField",
                defaultValue: "Terms Of Use",
            },
            {
                fieldType: "General",
                name: fields.termsLink,
                displayName: "terms and Conditions Url",
                editorTemplate: "LinkField",
                defaultValue: { type: "Url", value: "" },
            },
        ],
    },
};

export default widgetModule;
